import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const location = useLocation();

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <div className="w-full max-w-md text-center">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary-100 flex items-center justify-center">
          <i className="ri-shield-keyhole-line text-3xl text-secondary-500"></i>
        </div>

        {/* Message */}
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
          {t("auth.sign_in_to_continue")}
        </h2>
        <p className="text-sm text-foreground-500 mb-8 max-w-sm mx-auto leading-relaxed">
          {t("auth.please_sign_in")}
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/login"
            state={{ from: location.pathname }}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-login-box-line"></i>
            {t("auth.sign_in")}
          </Link>
          <Link
            to="/register"
            state={{ from: location.pathname }}
            className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-foreground-200 text-foreground-700 font-semibold rounded-full hover:border-foreground-400 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-user-add-line"></i>
            {t("auth.sign_up")}
          </Link>
        </div>

        {/* Back to home */}
        <div className="mt-8">
          <Link
            to="/"
            className="text-sm text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1 justify-center"
          >
            <i className="ri-arrow-left-line"></i>
            {t("nav.home")}
          </Link>
        </div>
      </div>
    </div>
  );
}